import React, { useState, useCallback } from 'react';
import { Form, Input, Button, Typography, Divider, notification, Checkbox } from 'antd';
import { LockOutlined, UserOutlined, EyeInvisibleOutlined, EyeTwoTone, CloseCircleOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import ajaxService from '../../services/ajax-service';
import AuthLayout from './AuthLayout';

const { Text } = Typography;

const Login = ({ onLogin }) => {
    const history = useHistory();
    const location = useLocation();
    const { from } = location.state || { from: { pathname: "/" } };

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const showError = useCallback((message) => {
        notification.open({
            message,
            icon: <CloseCircleOutlined style={{ color: 'red' }} />,
            duration: 3,
        });
    }, []);

    const onFinish = useCallback(async (values) => {
        try {
            setLoading(true);
            const response = await ajaxService.getAccessToken(values.email, values.password);
            console.log('Login response:', { success: response.success, message: response.message }); // Avoid logging sensitive data

            if (response.success) {
                const { name, role, token, court_id } = response.data;
                if (court_id) {
                    localStorage.setItem('courtID', court_id);
                }
                localStorage.setItem('name', name);
                localStorage.setItem('role', role);
                localStorage.setItem('token', token);

                if (values.rememberMe) {
                    localStorage.setItem('rememberedEmail', values.email);
                } else {
                    localStorage.removeItem('rememberedEmail');
                }

                onLogin();
                history.replace(from);
            } else {
                if (response.message === 'Unauthenticated') {
                    showError(response.data?.error || 'Authentication failed');
                } else {
                    // Handle validation errors more gracefully
                    const errors = Object.values(response.data || {}).flat();
                    errors.forEach((_error) => {
                        showError('Invalid login credentials');
                    });
                }
            }
        } catch (error) {
            console.error('Login error:', error); // Log error without sensitive info
            showError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [history, from, onLogin, showError]);

    const onFinishFailed = useCallback((errorInfo) => {
        console.log('Form validation failed:', errorInfo);
        // Optionally show a general error for form issues
        if (errorInfo.errorFields.length > 0) {
            showError('Please check your input and try again.');
        }
    }, [showError]);

    const formContent = (props) => {
        const { formItemStyle, buttonItemStyle, dividerItemStyle, forgotDivStyle, textSize, formSize, spacing, buttonHeight } = props;
        return (
            <>
                <Form
                    form={form}
                    name="login"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    initialValues={{
                        email: typeof window !== 'undefined' && localStorage.getItem('rememberedEmail') ? localStorage.getItem('rememberedEmail') : (location.email || ''),
                        rememberMe: !!(typeof window !== 'undefined' && localStorage.getItem('rememberedEmail')),
                    }}
                    layout="vertical"
                    size={formSize}
                    autoComplete="on"
                    aria-label="Login form"
                    style={{ 
                        margin: 0,
                    }}
                >
                    <Form.Item
                        name="email"
                        label={
                            <span style={{ 
                                color: 'var(--text-white)', 
                                fontWeight: 500, 
                                fontSize: textSize,
                                display: 'block',
                                marginBottom: '1px', // Even tighter label spacing
                            }}>
                                Email Address
                            </span>
                        }
                        rules={[
                            { type: 'email', message: 'Please enter a valid email address!' },
                            { required: true, message: 'Please enter your email address!' },
                        ]}
                        hasFeedback
                        style={formItemStyle}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: 'var(--text-gray-light)' }} />}
                            placeholder="admin@tandemly.com"
                            autoComplete="email"
                            style={{
                                borderRadius: '12px',
                                background: '#fff',
                                border: '1px solid rgba(254, 2, 125, 0.16)',
                                color: 'var(--text-white)',
                                height: 44,
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label={
                            <span style={{ 
                                color: 'var(--text-white)', 
                                fontWeight: 500, 
                                fontSize: textSize,
                                display: 'block',
                                marginBottom: '1px',
                            }}>
                                Password
                            </span>
                        }
                        rules={[{ required: true, message: 'Please enter your password!' }]}
                        hasFeedback
                        style={formItemStyle}
                    >
                        <Input.Password
                            prefix={<LockOutlined style={{ color: 'var(--text-gray-light)' }} />}
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            style={{
                                borderRadius: '12px',
                                background: '#fff',
                                border: '1px solid rgba(254, 2, 125, 0.16)',
                                color: 'var(--text-white)',
                                height: 44,
                            }}
                        />
                    </Form.Item>

                    <Form.Item name="rememberMe" valuePropName="checked" style={{ marginBottom: spacing }}>
                        <Checkbox
                            style={{
                                color: 'var(--text-gray-light)',
                                fontSize: textSize,
                            }}
                        >
                            Remember me
                        </Checkbox>
                    </Form.Item>

                    <Form.Item style={{...buttonItemStyle, marginTop: spacing}}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            size={formSize}
                            style={{
                                height: Math.max(parseInt(buttonHeight, 10) || 40, 44),
                                borderRadius: '12px',
                                marginTop: spacing,
                                background: 'var(--gradient-primary)',
                                border: 'none',
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                boxShadow: '0 6px 18px rgba(254, 2, 125, 0.28)',
                            }}
                            aria-label={loading ? 'Signing in' : 'Sign in'}
                        >
                            {loading ? 'Signing In...' : 'Sign In'}
                        </Button>
                    </Form.Item>

                    <Divider 
                        plain 
                        style={dividerItemStyle}
                    >
                        <Text style={{ color: 'var(--text-gray-light)', fontSize: textSize }}>or</Text>
                    </Divider>

                    <div style={forgotDivStyle}>
                        <Button
                            type="link"
                            onClick={() => history.push({ pathname: '/forgot-password' })}
                            style={{
                                color: 'var(--primary-purple-light)',
                                fontWeight: 500,
                                padding: 0,
                                fontSize: textSize,
                            }}
                            aria-label="Forgot password"
                        >
                            Forgot your password?
                        </Button>
                    </div>
                </Form>
            </>
        );
    };

    return (
        <AuthLayout
            title="Welcome Back"
            subtitle="Sign in to your admin dashboard"
            formContent={formContent}
            onError={showError}
        />
    );
};

export default React.memo(Login);